import { CsvFileClientErrorFlags } from '../components/campaign/CsvFileClientErrorDialog'

// ---------- Client-side CSV pre-validation constants ----------
export const CSV_MAX_SIZE_MB = 100 
export const CSV_MAX_SIZE_BYTES = CSV_MAX_SIZE_MB * 1024 * 1024
export const CSV_MAX_ROWS = 100_000
export const CSV_MAX_COLUMNS = 50

export interface CsvValidationResult {
  error: CsvFileClientErrorFlags | null
  headers: string[]
  totalRows: number
  lines: string[]
}



/** Fast chunk-based scanner — returns CsvValidationResult containing error flags, headers, totalRows, and lines. */
export async function validateCsvFile(file: File): Promise<CsvValidationResult> {
  const emptyResult: CsvValidationResult = {
    error: null,
    headers: [],
    totalRows: 0,
    lines: [],
  }

  if (!file.name.toLowerCase().endsWith('.csv')) {
    return { ...emptyResult, error: { file_type: true } }
  }
  if (file.size > CSV_MAX_SIZE_BYTES) {
    return { ...emptyResult, error: { file_size: true } }
  }
  if (file.size === 0) {
    return { ...emptyResult, error: { file_empty: true } }
  }

  // Read first 64 KB to check column count & header lines
  const headerSlice = file.slice(0, 65536)
  const headerText = await headerSlice.text()
  const lines = headerText.split('\n').map((l) => l.trim()).filter(Boolean)

  if (lines.length === 0) {
    return { ...emptyResult, error: { file_empty: true } }
  }

  const headers = lines[0].split(',').map((h) => h.trim().replace(/^["']|["']$/g, ''))
  if (headers.length > CSV_MAX_COLUMNS) {
    return { ...emptyResult, error: { column_limit: true }, headers, lines }
  }

  // Fast newline scan in 1 MB chunks to get total line count — avoids loading large files
  let lineCount = 0
  let lastChar = ''
  const CHUNK = 1024 * 1024
  for (let offset = 0; offset < file.size; offset += CHUNK) {
    const chunk = await file.slice(offset, offset + CHUNK).text()
    for (let i = 0; i < chunk.length; i++) {
      if (chunk[i] === '\n') lineCount++
    }
    if (chunk.length > 0) lastChar = chunk[chunk.length - 1]
    // Allow up to CSV_MAX_ROWS data rows + 1 header row
    if (lineCount > CSV_MAX_ROWS + 1) {
      return { ...emptyResult, error: { row_limit: true }, headers, lines }
    }
  }

  // Handle files without trailing \n at EOF (e.g. Google Sheets CSV export)
  if (file.size > 0 && lastChar !== '\n') {
    lineCount++
  }

  // Subtract 1 for the header row to get actual data record count
  const dataRows = lineCount > 0 ? lineCount - 1 : 0

  if (dataRows <= 0) {
    return { ...emptyResult, error: { file_empty: true }, headers, lines }
  }

  return {
    error: null,
    headers,
    totalRows: dataRows,
    lines,
  }
}
